import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, Phone, Droplet, Activity, Footprints, Thermometer,
  PackageCheck, CalendarClock, ShieldCheck, CheckCircle2, Loader2, Timer,
} from "lucide-react";
import PublicSiteHeader from "@/components/PublicSiteHeader";
import SEO, { diabetesCareSchema, diabetesFaqSchema } from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = `${API}/api/static/email_assets`;

const SUPPLY = [
  {
    key: "kit",
    eyebrow: "The meter",
    name: "Blood Glucose Monitoring System Kit",
    sku: "DMD1079WHT · Vive Precision",
    body: "Ships complete so it can be handed to a patient at discharge without a second order: meter, five-level lancing device, 25 test strips, 25 lancets, carrying case and batteries.",
    points: ["Result in 5 seconds, no coding step", "0.7 µL sample", "500 readings stored with date and time", "Pairs with the Vive Precision app"],
    img: `${IMG}/nov26_rh_glucose_kit.jpg`,
    to: "/marketplace/product/vive-dmd1079wht-glucose-monitor-kit",
  },
  {
    key: "strips",
    eyebrow: "The refill that keeps it alive",
    name: "Blood Glucose Test Strips — 50 ct / 100 ct",
    sku: "DMD1082BLU50CT · DMD1082BLU100CT",
    body: "No-code, pre-calibrated strips matched to the meter. Stocked in both counts so a program can set a refill cadence instead of reordering the week a patient runs dry.",
    points: ["50 ct (2 × 25) and 100 ct (2 × 50)", "Pre-calibrated — no setup error", "Storage range 39–86 °F, under 85% RH", "Standing refill schedules available"],
    img: `${IMG}/nov26_rh_diabetes_intro.jpg`,
    to: "/marketplace/product/vive-dmd1082-glucose-test-strips",
  },
  {
    key: "lancets",
    eyebrow: "The part that decides adherence",
    name: "Easy-Twist Lancets & 5-Level Lancing Device",
    sku: "Vive Precision · sterile single-use",
    body: "Twist-off cap, sterile single-use, universal fit. Five depth settings let the stick be matched to the patient's skin instead of defaulting to the deepest — the single biggest reason patients quit testing.",
    points: ["Twist-off cap, no exposed tip", "Five adjustable depth levels", "Sterile, single-use", "Bulk counts for program volume"],
    img: `${IMG}/nov26_rh_glucose_testing.jpg`,
    to: "/marketplace/product/vive-easy-twist-lancets",
  },
];

const BEYOND = [
  {
    icon: Footprints,
    title: "Feet and skin",
    body: "Neuropathy means the patient cannot feel the problem starting. A daily foot check is the cheapest intervention in the program — it needs a light, a mirror and somebody who was told to do it.",
    points: ["Daily foot inspection guidance", "Cushioning and offloading", "Skin and wound care supplies"],
  },
  {
    icon: Activity,
    title: "Blood pressure",
    body: "The diabetic patient in your panel is usually hypertensive too. Upper-arm monitors in standard through bariatric cuffs, sized at enrollment instead of guessed — and the same app holds both readings.",
    points: ["Validated upper-arm monitors", "Standard to bariatric cuffs stocked", "One app for glucose and BP"],
  },
  {
    icon: Thermometer,
    title: "Weight, oximetry, temperature",
    body: "Complications announce themselves before the ED does. Daily weights catch fluid, oximetry catches perfusion, and a thermometer catches the infection that started quietly in a foot.",
    points: ["High-capacity and talking scales", "Fingertip oximeters — SpO2 and pulse", "Infrared and oral thermometers"],
  },
];

const TEACHING = [
  {
    n: "01",
    title: "Wash, rinse, dry completely",
    body: "Fruit juice, lotion or sugar residue on a fingertip reads high — sometimes dramatically. Alcohol alone leaves a residue of its own if it has not evaporated.",
  },
  {
    n: "02",
    title: "Side of the fingertip, and rotate",
    body: "The side hurts less and bleeds more readily than the pad. Rotating fingers prevents the callusing that makes testing progressively more painful until the patient stops.",
  },
  {
    n: "03",
    title: "Match the depth to the skin",
    body: "This is what the five levels on the lancing device are for. Defaulting to the deepest setting is how a program teaches its patients that testing hurts.",
  },
  {
    n: "04",
    title: "Store the strips like reagent chemistry",
    body: "Left in a hot car or a steamy bathroom, strips drift — and the meter has no way to tell you. 39–86 °F, under 85% humidity, vial capped, expiry checked at every refill.",
  },
];

const FAQ = [
  {
    q: "What diabetes care products does Sterile Mate supply?",
    a: "The full testing chain: Vive Precision blood glucose monitoring kits (meter, five-level lancing device, test strips, lancets, carry case and batteries), replacement test strips in 50 and 100 count, easy-twist lancets, plus the Sol-M and Sol-Care diabetes care line of safety lancets, safety insulin syringes, standard insulin syringes, pen needles and alcohol prep pads.",
  },
  {
    q: "How long does a glucose kit's test strips last?",
    a: "A standard kit ships with 25 test strips and 25 lancets. At twice-daily testing that is roughly twelve days, which is why we stage strip and lancet refills with the kit rather than after it. Strips are stocked in 50 count (2 × 25) and 100 count (2 × 50) packs with standing refill schedules available.",
  },
  {
    q: "Do you ship diabetes supplies directly to the patient's home?",
    a: "Yes. Kits and refills can be drop-shipped to a patient address with tracking returned to the case manager, or palletized in bulk to a clinic or program site. No GPO contract is required either way.",
  },
  {
    q: "How fast is the Vive Precision blood glucose meter?",
    a: "The DMD1079WHT returns a result in 5 seconds from a 0.7 µL sample using no-code, pre-calibrated strips, and stores up to 500 readings with date and time. It pairs with the Vive Precision app, which also holds blood pressure readings.",
  },
  {
    q: "Is pricing published for diabetes care products?",
    a: "No. Kits, strips and lancets are quoted against your program's enrollment volume so the meter and its consumables are priced together as one program rather than three separate line items you reconcile later.",
  },
];

const CREDS = [
  { icon: ShieldCheck, label: "FDA-registered manufacturer · Vive Precision" },
  { icon: PackageCheck, label: "No GPO contract required" },
  { icon: Timer, label: "Drop-ship to the patient or bulk to the clinic" },
  { icon: CalendarClock, label: "Standing refill schedules, held and released" },
];

function DiabetesLeadForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "Program pricing", message: "" });
  const [status, setStatus] = useState("idle");
  const [errMsg, setErrMsg] = useState("");

  const interests = [
    "Program pricing",
    "Diabetes line sheet",
    "Patient teaching sheet",
    "Holiday kit staging",
    "Something else",
  ];

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrMsg("");
    try {
      const res = await fetch(`${API}/api/leads/coming-soon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: "diabetes-management-program",
          product_name: `Diabetes Management — ${form.interest}`,
          email: form.email.trim(),
          name: form.name.trim() || null,
          company: form.company.trim() || null,
          message: form.message.trim() || null,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || "Submission failed");
      }
      setStatus("success");
      setForm({ name: "", email: "", company: "", interest: "Program pricing", message: "" });
    } catch (err) {
      setStatus("error");
      setErrMsg(err.message || "Something went wrong — please call 281.456.3458.");
    }
  };

  const field = "w-full rounded-lg bg-white border border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25 focus:outline-none text-slate-900 text-sm px-4 py-3 placeholder:text-slate-400 transition-all";

  return (
    <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start" data-testid="diabetes-lead-form-section">
      <div className="lg:col-span-2">
        <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-3">Start a program</p>
        <h2 className="text-3xl lg:text-4xl font-black text-slate-900 leading-[1.1] tracking-tight mb-5">
          Tell us how many patients<br />
          <span className="text-sky-600">and we will price the refills with it.</span>
        </h2>
        <p className="text-slate-600 text-sm leading-relaxed mb-8">
          Kits, strips and lancets priced as one program at your enrollment volume — not three
          separate line items you reconcile later. A senior team member replies within one
          business day.
        </p>
        <div className="space-y-3 text-sm">
          <a href="tel:2814563458" data-testid="diabetes-form-phone" className="flex items-center gap-3 text-slate-700 font-semibold hover:text-sky-600 transition-colors">
            <Phone className="w-4 h-4 text-sky-600" /> 281.456.3458
          </a>
          <a href="mailto:orders@sterilemate.com?subject=Diabetes%20Management%20Program" data-testid="diabetes-form-email" className="flex items-center gap-3 text-slate-700 font-semibold hover:text-sky-600 transition-colors">
            <Mail className="w-4 h-4 text-sky-600" /> orders@sterilemate.com
          </a>
        </div>
      </div>

      <form onSubmit={onSubmit} className="lg:col-span-3 rounded-2xl bg-white border border-slate-200 shadow-lg shadow-slate-200/60 p-7 lg:p-9 space-y-4" data-testid="diabetes-lead-form">
        {status === "success" ? (
          <div className="py-10 text-center" data-testid="diabetes-form-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-black text-slate-900 mb-2">Got it — thank you.</h3>
            <p className="text-slate-600 text-sm">
              We will come back within one business day with program pricing and a refill cadence.
            </p>
            <button
              type="button"
              onClick={() => setStatus("idle")}
              data-testid="diabetes-form-reset"
              className="mt-6 text-xs font-bold text-sky-600 hover:text-sky-700 underline"
            >
              Send another request
            </button>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dm-name" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Name</label>
                <input id="dm-name" name="name" value={form.name} onChange={onChange} className={field} placeholder="Jane Alvarez" data-testid="diabetes-form-name" />
              </div>
              <div>
                <label htmlFor="dm-email" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Work email *</label>
                <input id="dm-email" name="email" type="email" required value={form.email} onChange={onChange} className={field} placeholder="jalvarez@hospital.org" data-testid="diabetes-form-email-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="dm-company" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Facility</label>
                <input id="dm-company" name="company" value={form.company} onChange={onChange} className={field} placeholder="Memorial Health — Case Management" data-testid="diabetes-form-company" />
              </div>
              <div>
                <label htmlFor="dm-interest" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">What do you need?</label>
                <select id="dm-interest" name="interest" value={form.interest} onChange={onChange} className={field} data-testid="diabetes-form-interest">
                  {interests.map((i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="dm-message" className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Enrollment volume or notes</label>
              <textarea id="dm-message" name="message" rows={4} value={form.message} onChange={onChange} className={field} placeholder="About 40 diabetic discharges a month. We currently send patients home with a meter only." data-testid="diabetes-form-message" />
            </div>
            {status === "error" && (
              <p className="text-sm text-rose-600" data-testid="diabetes-form-error">{errMsg}</p>
            )}
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="diabetes-form-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:opacity-60 text-white font-black text-sm transition-colors shadow-md shadow-sky-600/20"
            >
              {status === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request program pricing <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-[11px] text-slate-500 text-center">
              No pricing is published on this page. Everything is quoted against your volume.
            </p>
          </>
        )}
      </form>
    </div>
  );
}

export default function DiabetesManagementPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="diabetes-management-page">
      <SEO
        title="Diabetes Care Products & Glucose Monitors for Hospitals and Home Health"
        description="Diabetes management supplies from Sterile Mate: Vive Precision blood glucose monitors, test strips (50 & 100 ct), easy-twist lancets, insulin syringes, pen needles and safety lancets — plus BP monitors, scales and oximeters. Program pricing, drop-ship to the patient, no GPO contract required. Houston, TX."
        canonical="/solutions/diabetes-management"
        image="https://sterilemate.com/og-default.jpg"
        keywords={[
          "diabetes care products",
          "diabetes management supplies hospital",
          "blood glucose monitor hospital supplier",
          "blood glucose test strips distributor",
          "Vive Precision glucose monitor distributor",
          "glucose meter kit for discharge",
          "diabetic discharge kit supplier",
          "blood glucose monitoring system bulk",
          "test strips 50 count 100 count wholesale",
          "easy twist lancets distributor",
          "safety lancets hospital",
          "insulin syringes distributor",
          "insulin pen needles distributor",
          "diabetic foot care supplies",
          "remote patient monitoring diabetes supplies",
          "home health diabetes supplies Texas",
          "diabetes supplies non-GPO",
          "glucose monitoring program pricing",
          "diabetes management Houston medical supplier",
        ]}
        schema={[diabetesCareSchema, diabetesFaqSchema]}
      />
      <PublicSiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white border-b border-slate-200">
        {/* blue-circle motif */}
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 w-[420px] h-[420px] max-w-full rounded-full border-[26px] border-sky-200/60 hidden lg:block pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-5" data-testid="diabetes-hero-eyebrow">
                Diabetes Management · Sterile Mate
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.04] tracking-tight mb-7" data-testid="diabetes-hero-headline">
                Diabetes Management<br />
                <span className="text-sky-600">from Sterile Mate.</span>
              </h1>
              <p className="text-lg text-slate-700 leading-relaxed mb-5 max-w-2xl">
                We supply the whole testing chain — not just the meter that gets photographed, but
                the strips and lancets that quietly decide whether the program survives past day
                ninety.
              </p>
              <p className="text-sm text-slate-500 leading-relaxed mb-9 max-w-2xl">
                Blood glucose kits, test strips in 50 and 100 count, easy-twist lancets, insulin
                syringes and pen needles — with blood pressure monitors, scales and oximeters
                alongside, because the diabetic patient in your panel rarely has only one chart.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <a
                  href="#diabetes-request"
                  data-testid="diabetes-hero-cta-pricing"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-black text-sm transition-colors shadow-md shadow-sky-600/25"
                >
                  Request program pricing <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/marketplace?category=home-health-aids&sub=Glucose%20Monitors"
                  data-testid="diabetes-hero-cta-browse"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-xl border border-slate-300 bg-white hover:border-sky-500 hover:text-sky-700 text-slate-800 text-sm font-bold transition-colors"
                >
                  Browse glucose monitors
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl shadow-slate-300/40 aspect-[4/3]">
                <img
                  src={`${IMG}/nov26_rh_diabetes_intro.jpg`}
                  alt="Blood glucose monitoring kit — meter, lancing device, test strips and lancets"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE THESIS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-4">Why we built this line</p>
            <h2 className="text-3xl lg:text-[2.6rem] font-black text-slate-900 leading-[1.1] tracking-tight">
              The meter is rarely<br />what fails.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[15px] text-slate-600 leading-relaxed">
            <p>
              A program buys meters, hands them out at discharge, and discovers ninety days later
              that half the patients stopped testing. Not because the device broke — because the
              strips ran out and nobody owned the refill.
            </p>
            <p className="text-slate-800">
              So we built the line around the consumables. A kit ships with 25 strips. At twice
              daily testing that is <strong className="text-slate-900 font-black">twelve days</strong>.
              Unless the refill is planned with the kit, the patient rations tests or quits — and
              the program reports an adherence problem that was really a supply problem.
            </p>
            <p>
              Strips in 50 and 100 count, lancets in program volumes, standing refill schedules
              we hold and release on your calendar. No GPO contract required, drop-shipped to the
              patient's home or palletized to your clinic, whichever your program runs on.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE SUPPLY */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-4">What we supply</p>
            <h2 className="text-3xl lg:text-[2.6rem] font-black text-slate-900 leading-[1.1] tracking-tight">
              Three things, and the second two<br />
              <span className="text-sky-600">are the ones that matter.</span>
            </h2>
          </div>

          <div className="space-y-6">
            {SUPPLY.map((s, i) => (
              <div
                key={s.key}
                className="group grid lg:grid-cols-12 gap-0 rounded-2xl overflow-hidden bg-white border border-slate-200 hover:border-sky-400 hover:shadow-xl hover:shadow-slate-300/40 transition-all duration-300"
                data-testid={`diabetes-supply-${s.key}`}
              >
                <div className={`lg:col-span-4 relative min-h-[220px] bg-slate-100 ${i % 2 === 1 ? "lg:order-last" : ""}`}>
                  <img src={s.img} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="lg:col-span-8 p-8 lg:p-11">
                  <p className="text-[11px] font-black uppercase tracking-[2.5px] text-sky-600 mb-3">{s.eyebrow}</p>
                  <h3 className="text-2xl lg:text-[1.75rem] font-black text-slate-900 leading-tight tracking-tight mb-2">{s.name}</h3>
                  <p className="text-xs font-mono text-slate-500 mb-5">{s.sku}</p>
                  <p className="text-[15px] text-slate-600 leading-relaxed mb-6 max-w-2xl">{s.body}</p>
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-7">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-sm text-slate-700">
                        <Droplet className="w-3.5 h-3.5 text-sky-500 mt-0.5 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={s.to}
                    data-testid={`diabetes-supply-link-${s.key}`}
                    className="inline-flex items-center gap-2 text-sm font-black text-sky-600 hover:gap-3.5 transition-all"
                  >
                    See the product page <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-sky-200 bg-sky-50 p-7 lg:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div>
              <h3 className="text-lg font-black text-slate-900 mb-1.5">Also on the same purchase order</h3>
              <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
                Safety lancets, safety insulin syringes, standard insulin syringes, pen needles
                and alcohol prep pads — the Sol-M and Sol-Care diabetes care line, already stocked.
              </p>
            </div>
            <Link
              to="/marketplace?category=home-health-aids&sub=Diabetes%20Care"
              data-testid="diabetes-care-line-link"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white border border-slate-300 hover:border-sky-500 hover:text-sky-700 text-slate-800 text-sm font-bold transition-colors"
            >
              View the diabetes care line <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BEYOND THE METER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-4">Beyond the meter</p>
            <h2 className="text-3xl lg:text-[2.6rem] font-black text-slate-900 leading-[1.1] tracking-tight">
              The expensive admission<br />
              <span className="text-sky-600">is rarely a glucose reading.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-sm text-slate-600 leading-relaxed">
            It is a foot nobody looked at, an infection that started as a blister, or fluid that
            climbed for a week before anyone weighed the patient. These belong in the diabetes
            kit, not in a separate purchase order.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {BEYOND.map((b) => {
            const I = b.icon;
            return (
              <div
                key={b.title}
                className="rounded-2xl bg-white border border-slate-200 p-8 hover:border-sky-400 hover:shadow-xl hover:shadow-slate-300/40 hover:-translate-y-1 transition-all duration-300"
                data-testid={`diabetes-beyond-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="w-12 h-12 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center mb-6">
                  <I className="w-5 h-5 text-sky-600" />
                </div>
                <h3 className="text-xl font-black text-slate-900 leading-tight tracking-tight mb-3">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{b.body}</p>
                <ul className="space-y-2">
                  {b.points.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-xs text-slate-500">
                      <span className="inline-block w-1 h-1 rounded-full bg-sky-500 mt-1.5 shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="relative rounded-2xl overflow-hidden min-h-[300px] border border-slate-200 shadow-lg shadow-slate-200/60">
            <img src={`${IMG}/nov26_rh_diabetic_footcare.jpg`} alt="Home health nurse performing a diabetic foot check" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-white/92 backdrop-blur-sm border-t border-slate-200 p-7">
              <p className="text-[11px] font-black uppercase tracking-[2.5px] text-sky-600 mb-1.5">The daily check</p>
              <p className="text-slate-900 text-lg font-bold leading-snug">A light, a mirror, and somebody who was told to do it.</p>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden min-h-[300px] border border-slate-200 shadow-lg shadow-slate-200/60">
            <img src={`${IMG}/nov26_rh_nurse_teaching.jpg`} alt="Nurse teaching a patient to use a glucose meter at discharge" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-white/92 backdrop-blur-sm border-t border-slate-200 p-7">
              <p className="text-[11px] font-black uppercase tracking-[2.5px] text-sky-600 mb-1.5">At discharge</p>
              <p className="text-slate-900 text-lg font-bold leading-snug">One page a family member can follow — in the box, not emailed later.</p>
            </div>
          </div>
        </div>
      </section>

      {/* TEACHING */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="max-w-2xl mb-14">
            <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-4">Patient teaching</p>
            <h2 className="text-3xl lg:text-[2.6rem] font-black text-slate-900 leading-[1.1] tracking-tight mb-5">
              Four things that cause<br />
              <span className="text-sky-600">most of the bad readings.</span>
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              When a patient's home readings do not match the clinic, the device is usually fine.
              Each of these is teachable in about five minutes, and we will send the one-page
              version written for a patient rather than a clinician.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10">
            {TEACHING.map((t) => (
              <div key={t.n} className="flex gap-6" data-testid={`diabetes-teaching-${t.n}`}>
                <span className="text-4xl font-black text-sky-300 leading-none shrink-0 tabular-nums">{t.n}</span>
                <div>
                  <h3 className="text-lg font-black text-slate-900 leading-tight mb-2">{t.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOLIDAY / STAGING BAND */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-8 lg:p-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="text-[11px] font-black uppercase tracking-[3px] text-amber-700 mb-4">Plan the refill, not just the kit</p>
              <h3 className="text-2xl lg:text-3xl font-black text-slate-900 leading-tight tracking-tight mb-4">
                Twenty-five strips is under four weeks.
              </h3>
              <p className="text-slate-700 text-[15px] leading-relaxed max-w-2xl">
                Census climbs, staffing thins and carriers run holiday blackout days. The box a
                patient needs on December 22nd realistically has to be in your building by the
                12th. We build the kits, stage the strip and lancet refills with them, hold the
                lot and release on your schedule.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <a
                href="#diabetes-request"
                data-testid="diabetes-staging-cta"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-900 font-black text-sm transition-colors shadow-md shadow-amber-500/30"
              >
                Stage my kits <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="bg-sky-50 border-y border-sky-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {CREDS.map((c) => {
              const I = c.icon;
              return (
                <div key={c.label} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white border border-sky-200 flex items-center justify-center shrink-0">
                    <I className="w-4 h-4 text-sky-600" />
                  </div>
                  <p className="text-xs text-slate-700 leading-snug font-semibold">{c.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ — mirrors diabetesFaqSchema so the structured data matches the page */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="diabetes-faq-section">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-[11px] font-black uppercase tracking-[3px] text-sky-600 mb-4">Common questions</p>
            <h2 className="text-3xl lg:text-[2.4rem] font-black text-slate-900 leading-[1.1] tracking-tight">
              What buyers ask<br />
              <span className="text-sky-600">before the first order.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 divide-y divide-slate-200 border-t border-slate-200">
            {FAQ.map((f, i) => (
              <div key={f.q} className="py-7" data-testid={`diabetes-faq-${i + 1}`}>
                <h3 className="text-lg font-black text-slate-900 leading-snug mb-3">{f.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-3xl">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="diabetes-request" className="bg-slate-50 border-t border-slate-200 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <DiabetesLeadForm />
        </div>
      </section>
    </div>
  );
}
